import { useState } from 'react';
import {
  Box, Typography, TextField, Button, Checkbox, FormControlLabel,
  IconButton, InputAdornment, Alert, Divider,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTheme } from '@mui/material/styles';
import { useApp } from '../context/AppContext';
import { useThemeMode } from '../main';

const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1';

const FEATURES = [
  { icon: '🚀', title: 'Per-environment toggles', desc: 'Dev, staging, prod — controlled independently.' },
  { icon: '🔗', title: 'Feature dependencies', desc: 'Chain features so they only activate when dependencies are met.' },
  { icon: '⚡', title: 'Instant rollouts', desc: 'Toggle flags in real-time without redeploying your code.' },
];

async function authenticateWithCognito(email: string, password: string): Promise<string> {
  const res = await fetch(`https://cognito-idp.${REGION}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Authentication failed');
  }
  const data = await res.json();
  return data.AuthenticationResult.AccessToken;
}

export default function LoginPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { login } = useApp();
  const { toggleTheme, mode: themeMode } = useThemeMode();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const borderColor = isDark ? '#2C2C2E' : '#E5E5EA';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    try {
      const token = await authenticateWithCognito(email, password);
      login(email, token);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* LEFT BRAND PANEL */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        width: '55%',
        position: 'relative',
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(145deg, #0A0A0A 0%, #1A1A1A 40%, #0D1A1A 100%)'
          : 'linear-gradient(145deg, #1C1C1E 0%, #2C2C2E 40%, #003D3B 100%)',
        p: 6,
        justifyContent: 'space-between',
      }}>
        {/* Decorative blobs */}
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,59,48,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -120, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,199,190,0.14) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{ bgcolor: '#FF3B30', p: 0.75, borderRadius: 2, display: 'flex' }}>
            <FlagIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, letterSpacing: '0.08em', color: '#F5F5F7', fontSize: 20 }}>HAKI</Typography>
        </Box>

        {/* Hero */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontSize: { md: 36, lg: 44 }, fontWeight: 800, color: '#F5F5F7', lineHeight: 1.15, letterSpacing: '-0.02em', mb: 2.5 }}>
            Feature flags<br />
            <Box component="span" sx={{ color: '#FF3B30' }}>for the bold.</Box>
          </Typography>
          <Typography sx={{ color: '#8E8E93', mb: 5, maxWidth: 380, lineHeight: 1.6 }}>
            Deploy with confidence. Control every feature across every environment — instantly, without redeployment.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {FEATURES.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2.5, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#F5F5F7', mb: 0.25 }}>{f.title}</Typography>
                  <Typography sx={{ color: '#6E6E73', fontSize: 13, lineHeight: 1.5 }}>{f.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer quote */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ borderLeft: '3px solid #FF3B30', pl: 2 }}>
            <Typography sx={{ color: '#8E8E93', fontSize: 13, fontStyle: 'italic', lineHeight: 1.6 }}>
              "Ship features boldly — control who sees what, when."
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* RIGHT FORM PANEL */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: { xs: 3, sm: 6 }, position: 'relative' }}>
        {/* Theme toggle */}
        <IconButton onClick={toggleTheme} sx={{ position: 'absolute', top: 20, right: 20, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', border: `1px solid ${borderColor}`, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' } }}>
          {themeMode === 'dark' ? <LightModeIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <DarkModeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        </IconButton>

        {/* Mobile logo */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
          <Box sx={{ bgcolor: '#FF3B30', p: 0.75, borderRadius: 2, display: 'flex' }}>
            <FlagIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, letterSpacing: '0.08em', color: 'text.primary', fontSize: 20 }}>HAKI</Typography>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 800, color: 'text.primary', mb: 0.75, fontSize: 28, letterSpacing: '-0.01em' }}>Welcome back</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 15 }}>Sign in to your Haki workspace</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Email address" type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth autoComplete="email" placeholder="you@company.com"
              InputProps={{ sx: { borderRadius: 2 } }}
            />
            <TextField
              label="Password" type={showPassword ? 'text' : 'password'}
              value={password} onChange={e => setPassword(e.target.value)}
              fullWidth autoComplete="current-password" placeholder="••••••••"
              InputProps={{
                sx: { borderRadius: 2 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small">
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18, color: 'text.secondary' }} /> : <Visibility sx={{ fontSize: 18, color: 'text.secondary' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -0.5 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} sx={{ color: 'text.secondary', '&.Mui-checked': { color: 'secondary.main' } }} />}
                label={<Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Remember me</Typography>}
              />
              <Typography sx={{ fontSize: 14, color: 'secondary.main', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                Forgot password?
              </Typography>
            </Box>
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ py: 1.5, borderRadius: 2, mt: 0.5, fontWeight: 700, fontSize: 15 }}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
            <Divider sx={{ my: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 1 }}>secure login</Typography>
            </Divider>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? 'rgba(0,199,190,0.07)' : 'rgba(0,199,190,0.08)', border: `1px solid ${isDark ? 'rgba(0,199,190,0.2)' : 'rgba(0,199,190,0.25)'}`, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'secondary.main', mt: 0.1, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
                Sign in with your Cognito credentials to manage feature flags.
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography sx={{ position: 'absolute', bottom: 20, color: 'text.secondary', fontSize: 12 }}>
          © {new Date().getFullYear()} Haki — Feature Flag Control
        </Typography>
      </Box>
    </Box>
  );
}
